import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '@mui/material';
import { ApiClient } from '@provider-portal/common/api';
import {
  Compare,
  GetProvidersEligibilityDataResponse,
} from '@provider-portal/common/api/gen/CampaignDeliveryProvider';

import { EligibilityMetricCard, StatProgressBarVariant } from './EligibilityMetricCard';

interface MetricResult {
  target: number;
  value: number;
  operator: Compare;
}

const useStrings = (): EligibilityMetricsProps['strings'] => {
  const intl = useIntl();

  return useMemo(
    () => ({
      acceptanceRate: (target: number, thresholdExplanation: string) => {
        return {
          title: intl.formatMessage({
            id: 'components.promotionEligibility.acceptanceRate.title',
          }),
          thresholdExplanation: intl.formatMessage(
            { id: `components.promotionEligibility.acceptanceRate.${thresholdExplanation}` },
            { percent: target },
          ),
          achievementApproachPopup: intl.formatMessage({
            id: 'components.promotionEligibility.acceptanceRate.achievementApproachPopup',
          }),
          achievementApproachHtml: intl.formatMessage({
            id: 'components.promotionEligibility.acceptanceRate.achievementApproachHtml',
          }),
        };
      },
      availabilityRate: (target: number, thresholdExplanation: string) => {
        return {
          title: intl.formatMessage({
            id: 'components.promotionEligibility.availabilityRate.title',
          }),
          thresholdExplanation: intl.formatMessage(
            { id: `components.promotionEligibility.availabilityRate.${thresholdExplanation}` },
            { percent: target },
          ),
          achievementApproachPopup: intl.formatMessage({
            id: 'components.promotionEligibility.availabilityRate.achievementApproachPopup',
          }),
          achievementApproachHtml: intl.formatMessage({
            id: 'components.promotionEligibility.availabilityRate.achievementApproachHtml',
          }),
        };
      },
      ordersPreparedLate: (target: number, thresholdExplanation: string) => {
        return {
          title: intl.formatMessage({
            id: 'components.promotionEligibility.ordersPreparedLate.title',
          }),
          thresholdExplanation: intl.formatMessage(
            { id: `components.promotionEligibility.ordersPreparedLate.${thresholdExplanation}` },
            { percent: target },
          ),
          achievementApproachPopup: intl.formatMessage({
            id: 'components.promotionEligibility.ordersPreparedLate.achievementApproachPopup',
          }),
          achievementApproachHtml: intl.formatMessage({
            id: 'components.promotionEligibility.ordersPreparedLate.achievementApproachHtml',
          }),
        };
      },
      rating: (target: number, thresholdExplanation: string) => {
        return {
          title: intl.formatMessage({
            id: 'components.promotionEligibility.rating.title',
          }),
          thresholdExplanation: intl.formatMessage(
            { id: `components.promotionEligibility.rating.${thresholdExplanation}` },
            { value: target },
          ),
          achievementApproachPopup: intl.formatMessage({
            id: 'components.promotionEligibility.rating.achievementApproachPopup',
          }),
          achievementApproachHtml: intl.formatMessage({
            id: 'components.promotionEligibility.rating.achievementApproachHtml',
          }),
        };
      },
      photoCoverage: (target: number, thresholdExplanation: string) => {
        return {
          title: intl.formatMessage({
            id: 'components.promotionEligibility.photoCoverage.title',
          }),
          thresholdExplanation: intl.formatMessage(
            { id: `components.promotionEligibility.photoCoverage.${thresholdExplanation}` },
            { percent: target },
          ),
          achievementApproachPopup: intl.formatMessage({
            id: 'components.promotionEligibility.photoCoverage.achievementApproachPopup',
          }),
          achievementApproachHtml: intl.formatMessage({
            id: 'components.promotionEligibility.photoCoverage.achievementApproachHtml',
          }),
        };
      },
    }),

    [intl],
  );
};

export enum MetricName {
  ACCEPTANCE_RATE = 'acceptance_rate',
  AVAILABILITY_RATE = 'availability_rate',
  RATING = 'rating',
  PHOTO_COVERAGE = 'photo_coverage',
  COOKING_TIME_DELAY_RATE = 'cooking_time_delay_rate',
}

export interface MetricsCardStrings {
  title: string;
  thresholdExplanation: string;
  achievementApproachPopup: string;
  achievementApproachHtml: string;
}

export interface EligibilityMetricsProps {
  strings: {
    acceptanceRate: (target: number, thresholdExplanationString: string) => MetricsCardStrings;
    availabilityRate: (target: number, thresholdExplanationString: string) => MetricsCardStrings;
    ordersPreparedLate: (target: number, thresholdExplanationString: string) => MetricsCardStrings;
    rating: (target: number, thresholdExplanationString: string) => MetricsCardStrings;
    photoCoverage: (target: number, thresholdExplanationString: string) => MetricsCardStrings;
  };
}

export interface EligibilityMetricsFunctionalProps {
  apiClient: ApiClient;
  providerId: number;
}

function getCurrentMetricPercentage(metric: MetricResult): number {
  const step = 0.01;
  // 1. "step" is needed to pad the target for GT and LT metrics where target is achieved once value surpasses the target and not when value === target
  // 2. For LT and LE metrics lower value is preferred so percentage progress is calculated with value as base and target as current to find how much is achieved.
  //    When both value and target are closer, percentage progress will near hundred.
  // 3. For EQ metric we can check if value is above target or below it and then mimic same behaviour of GE or LE operator based on it.
  switch (metric.operator) {
    case Compare.GT: {
      const adjustedTarget = Math.floor((metric.target + step) * 100) / 100;
      const guardedCurrent = Math.min(metric.value, adjustedTarget);
      return Math.floor((guardedCurrent / adjustedTarget) * 100);
    }
    case Compare.GE: {
      const guardedCurrent = Math.min(metric.value, metric.target);
      return Math.floor((guardedCurrent / metric.target) * 100);
    }
    case Compare.LT: {
      const adjustedTarget = Math.floor((metric.target - step) * 100) / 100;
      const guardedCurrent = Math.max(metric.value, adjustedTarget);
      return Math.floor((adjustedTarget / guardedCurrent) * 100);
    }
    case Compare.LE: {
      const guardedCurrent = Math.max(metric.value, metric.target);
      return Math.floor((metric.target / guardedCurrent) * 100);
    }
    case Compare.EQ: {
      if (metric.value > metric.target) {
        const newMetric = { ...metric, operator: Compare.LE };
        return getCurrentMetricPercentage(newMetric);
      }
      if (metric.value < metric.target) {
        const newMetric = { ...metric, operator: Compare.GE };
        return getCurrentMetricPercentage(newMetric);
      }
      return 100;
    }
    default:
      return 100;
  }
}

function getThresholdExplanationString(operator: Compare): string {
  return [Compare.LE, Compare.GE].includes(operator)
    ? 'thresholdExplanationEq'
    : 'thresholdExplanation';
}

export const EligibilityMetrics = ({
  apiClient,
  providerId,
}: EligibilityMetricsFunctionalProps) => {
  const strings = useStrings();

  const [eligibilityMetrics, setEligibilityMetrics] =
    useState<GetProvidersEligibilityDataResponse['metrics_comparison_result']>(undefined);

  useEffect(() => {
    (async () => {
      let data: GetProvidersEligibilityDataResponse;
      try {
        data = await apiClient.promotions.getProviderEligibilityData({
          provider_id: providerId,
        });
      } catch (_e) {
        data = undefined;
      } finally {
        setEligibilityMetrics(data?.metrics_comparison_result);
      }
    })();
  }, [apiClient.promotions, providerId]);

  if (!eligibilityMetrics) {
    return null;
  }

  return (
    <Box
      display="grid"
      gap={3}
      gridTemplateColumns={{
        xs: 'repeat(auto-fit, minmax(250px, 1fr))',
        sm: 'repeat(auto-fit, minmax(330px, 1fr))',
        md: 'repeat(auto-fit, minmax(450px, 1fr))',
      }}
      width="100%"
    >
      {eligibilityMetrics[MetricName.ACCEPTANCE_RATE] && (
        <EligibilityMetricCard
          metrics={{
            current: Math.floor(eligibilityMetrics[MetricName.ACCEPTANCE_RATE].value * 100),
            target: Math.floor(eligibilityMetrics[MetricName.ACCEPTANCE_RATE].target * 100),
            percentage: getCurrentMetricPercentage(eligibilityMetrics[MetricName.ACCEPTANCE_RATE]),
            statProgressBarVariant: StatProgressBarVariant.CIRCULAR_PROGRESS_BAR,
          }}
          strings={strings.acceptanceRate(
            eligibilityMetrics[MetricName.ACCEPTANCE_RATE].target * 100,
            getThresholdExplanationString(eligibilityMetrics[MetricName.ACCEPTANCE_RATE].operator),
          )}
        />
      )}
      {eligibilityMetrics[MetricName.AVAILABILITY_RATE] && (
        <EligibilityMetricCard
          metrics={{
            current: Math.floor(eligibilityMetrics[MetricName.AVAILABILITY_RATE].value * 100),
            target: Math.floor(eligibilityMetrics[MetricName.AVAILABILITY_RATE].target * 100),
            percentage: getCurrentMetricPercentage(
              eligibilityMetrics[MetricName.AVAILABILITY_RATE],
            ),
            statProgressBarVariant: StatProgressBarVariant.CIRCULAR_PROGRESS_BAR,
          }}
          strings={strings.availabilityRate(
            eligibilityMetrics[MetricName.AVAILABILITY_RATE].target * 100,
            getThresholdExplanationString(
              eligibilityMetrics[MetricName.AVAILABILITY_RATE].operator,
            ),
          )}
        />
      )}
      {eligibilityMetrics[MetricName.COOKING_TIME_DELAY_RATE] && (
        <EligibilityMetricCard
          metrics={{
            current: Math.floor(eligibilityMetrics[MetricName.COOKING_TIME_DELAY_RATE].value * 100),
            target: Math.floor(eligibilityMetrics[MetricName.COOKING_TIME_DELAY_RATE].target * 100),
            percentage: getCurrentMetricPercentage(
              eligibilityMetrics[MetricName.COOKING_TIME_DELAY_RATE],
            ),
            statProgressBarVariant: StatProgressBarVariant.CIRCULAR_INFO_BAR,
          }}
          strings={strings.ordersPreparedLate(
            eligibilityMetrics[MetricName.COOKING_TIME_DELAY_RATE].target * 100,
            getThresholdExplanationString(
              eligibilityMetrics[MetricName.COOKING_TIME_DELAY_RATE].operator,
            ),
          )}
        />
      )}
      {eligibilityMetrics[MetricName.RATING] && (
        <EligibilityMetricCard
          metrics={{
            current: eligibilityMetrics[MetricName.RATING].value.toFixed(1),
            target: eligibilityMetrics[MetricName.RATING].target,
            percentage: getCurrentMetricPercentage(eligibilityMetrics[MetricName.RATING]),
            statProgressBarVariant: StatProgressBarVariant.CIRCULAR_INFO_BAR,
            haveStarInCircularInfoBar: true,
          }}
          strings={strings.rating(
            eligibilityMetrics[MetricName.RATING].target,
            getThresholdExplanationString(eligibilityMetrics[MetricName.RATING].operator),
          )}
        />
      )}
      {eligibilityMetrics[MetricName.PHOTO_COVERAGE] && (
        <EligibilityMetricCard
          metrics={{
            current: Math.floor(eligibilityMetrics[MetricName.PHOTO_COVERAGE].value * 100),
            target: Math.floor(eligibilityMetrics[MetricName.PHOTO_COVERAGE].target * 100),
            percentage: getCurrentMetricPercentage(eligibilityMetrics[MetricName.PHOTO_COVERAGE]),
            statProgressBarVariant: StatProgressBarVariant.CIRCULAR_PROGRESS_BAR,
          }}
          strings={strings.photoCoverage(
            eligibilityMetrics[MetricName.PHOTO_COVERAGE].target * 100,
            getThresholdExplanationString(eligibilityMetrics[MetricName.PHOTO_COVERAGE].operator),
          )}
        />
      )}
    </Box>
  );
};
